import React from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Chip,
  useMediaQuery,
  useTheme,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  PointOfSale as PosIcon,
  AccountBalanceWallet as CajaIcon,
  SwapVert as MovimientoIcon,
} from '@mui/icons-material';
import { usePosSession } from '../hooks/usePosSession';
import { CajaDialog } from './CajaDialog';
import { CashMovementDialog } from './CashMovementDialog';
import { usePosStore } from '../store/posStore';
import { formatCurrency } from '../utils/posCalculations';

export const PosLayout: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { sesionActiva, hasSesion, setCajaDialogOpen } = usePosSession();
  const { setCashMovementDialogOpen } = usePosStore();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      <AppBar position="static" sx={{ height: 48 }} elevation={2}>
        <Toolbar variant="dense" sx={{ minHeight: 48, px: isMobile ? 0.5 : 2 }}>
          <IconButton color="inherit" onClick={() => navigate('/dashboard')} size="small" sx={{ mr: 0.5 }}>
            <ArrowBackIcon />
          </IconButton>
          <PosIcon sx={{ mr: 0.5, fontSize: isMobile ? 20 : 24 }} />
          {!isMobile && (
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              Punto de Venta
            </Typography>
          )}

          {hasSesion && (
            <Chip
              label={isMobile ? (sesionActiva?.cajaNombre ?? 'Caja') : `Caja: ${sesionActiva?.cajaNombre ?? 'Sin nombre'}`}
              color="success"
              size="small"
              sx={{ ml: 1, maxWidth: isMobile ? 100 : 'none', fontSize: isMobile ? '0.65rem' : undefined }}
            />
          )}

          {hasSesion && !isMobile && (
            <Chip
              label={`Apertura: ${formatCurrency(sesionActiva?.montoApertura ?? 0)}`}
              size="small"
              variant="outlined"
              sx={{ ml: 1, color: 'white', borderColor: 'rgba(255,255,255,0.5)' }}
            />
          )}

          <Box sx={{ flexGrow: 1 }} />

          {hasSesion && (
            isMobile ? (
              <Tooltip title="Movimiento de caja">
                <IconButton color="inherit" size="small" onClick={() => setCashMovementDialogOpen(true)}>
                  <MovimientoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : (
              <Button color="inherit" startIcon={<MovimientoIcon />} size="small" onClick={() => setCashMovementDialogOpen(true)}>
                Movimiento
              </Button>
            )
          )}

          {isMobile ? (
            <Tooltip title={hasSesion ? 'Cerrar Caja' : 'Abrir Caja'}>
              <IconButton color="inherit" size="small" onClick={() => setCajaDialogOpen(true)}>
                <CajaIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Button color="inherit" startIcon={<CajaIcon />} size="small" onClick={() => setCajaDialogOpen(true)}>
              {hasSesion ? 'Cerrar Caja' : 'Abrir Caja'}
            </Button>
          )}
        </Toolbar>
      </AppBar>

      <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
        {hasSesion ? (
          <Outlet />
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              flexDirection: 'column',
              gap: 2,
              px: 2,
            }}
          >
            <PosIcon sx={{ fontSize: isMobile ? 48 : 64, color: 'text.secondary' }} />
            <Typography variant={isMobile ? 'h6' : 'h5'} color="text.secondary" textAlign="center">
              Debe abrir una caja para comenzar
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<CajaIcon />}
              onClick={() => setCajaDialogOpen(true)}
            >
              Abrir Caja
            </Button>
          </Box>
        )}
      </Box>

      <CajaDialog />
      <CashMovementDialog />
    </Box>
  );
};

export default PosLayout;
